import React, { useState, useRef, useEffect } from 'react';
import { formatDistanceToNow } from 'date-fns';
import ContentWithMentions from '../../common/ContentWithMentions';

const OriginalPostPreview = ({ post }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const contentRef = useRef(null);

  // Handle link clicks with event delegation
  useEffect(() => {
    const contentElement = contentRef.current;
    if (!contentElement) return;

    const handleLinkClick = (e) => {
      // Check if the clicked element is a link with our data attribute
      const link = e.target.closest('a[data-external-link="true"]');
      if (link) {
        e.preventDefault();
        e.stopPropagation();
        const href = link.getAttribute('href');
        if (href) {
          window.open(href, '_blank', 'noopener,noreferrer');
        }
      }
    };

    contentElement.addEventListener('click', handleLinkClick);
    
    return () => {
      contentElement.removeEventListener('click', handleLinkClick);
    };
  }, []);
  
  if (!post) return null;

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return formatDistanceToNow(date, { addSuffix: true });
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  const truncateContent = (content, maxLength = 280) => {
    if (!content) return '';
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  const shouldTruncate = post.content && post.content.length > 280;
  const displayContent = isExpanded ? post.content : truncateContent(post.content);

  return (
    <div className="original-post-preview border border-gray-200 dark:border-gray-700 rounded-lg p-3 mb-4">
      {/* Simple replying to indicator */}
      <div className="text-sm text-gray-500 dark:text-gray-400 mb-2">
        Replying to {post.type === 'question' && post.title ? `"${post.title}"` : 'post'}
      </div>

      {/* Author and timestamp - minimal */}
      <div className="flex items-center space-x-2 mb-2">
        <span className="text-sm font-medium text-gray-900 dark:text-white">
          {post.authorName}
        </span>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {formatDate(post.createdAt)}
        </span>
      </div>

      {/* Post content - focus on the text */}
      {post.content && (
        <div 
          ref={contentRef}
          className="text-sm text-gray-700 dark:text-gray-300"
        >
          <ContentWithMentions 
            content={displayContent} 
            className="prose-sm"
          />
          
          {shouldTruncate && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="mt-1 text-xs text-blue-600 dark:text-blue-400 hover:underline"
            >
              {isExpanded ? 'Show less' : 'Show more'}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default OriginalPostPreview;